import React, { useContext } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { Info, CheckCircle, ChevronLeft, ChevronRight, HelpCircle, ShieldCheck, X, Landmark } from "lucide-react";
import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Checkbox, Radio, TextField } from "components/Form/Field";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";
import { createRegisterUser, validateRegisterClientCesu } from "hooks/register";
import { getErrorResponseData } from "helpers/typing";
import { getPayloadErrorFromResponse } from "lib/serverError";
import { CodeCesu } from "types/cesu";
import { toast } from "sonner";
import CesuRadioOptionLabel, {
    cesuRadioGroupClassName,
    cesuRadioLabelClassName,
} from "components/Cesu/CesuRadioOptionLabel";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";

interface Props {
    client: RegisterDraft | undefined;
    setClient: (client: RegisterDraft) => void;
}

const isValidFrenchIban = (ibanRaw: string) => {
    const iban = String(ibanRaw ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

    // FR + 2 check digits + 23 BBAN chars (27 total)
    if (!/^FR\d{2}\d{10}[A-Z0-9]{11}\d{2}$/.test(iban)) return false;

    // IBAN checksum (ISO 13616): move first 4 chars to end, convert letters A=10..Z=35, mod 97 must be 1.
    const rearranged = iban.slice(4) + iban.slice(0, 4);

    let remainder = 0;
    for (const ch of rearranged) {
        const expanded = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch; // A->10 ... Z->35
        for (const digit of expanded) {
            remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
        }
    }

    return remainder === 1;
};

const isValidFrenchBic = (bicRaw: string) => {
    const bic = String(bicRaw ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

    // BIC (ISO 9362): 8 or 11 chars. For France, country code is "FR".
    // Format: 4 letters bank code + "FR" + 2 alnum location + optional 3 alnum branch.
    return /^[A-Z]{4}FR[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic);
};

const normalizeCodeCesu = (value: unknown): CodeCesu => {
    if (value === CodeCesu.ADD_CESU || value === CodeCesu.HAS_CESU) return value;

    // Backward compatibility with legacy values stored in drafts ("0"/"1").
    if (String(value ?? "") === "1") return CodeCesu.HAS_CESU;
    return CodeCesu.ADD_CESU;
};

const RegisterClientCesu: React.FC<Props> = ({ client, setClient }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    useRegisterDraftGuard(client, "prestations");

    const onSubmit = async (values: RegisterDraft) => {
        try {
            const rawSepa = (values as Record<string, unknown>)?.authorize_sepa;
            const rawAdmin = (values as Record<string, unknown>)?.authorize_admin;
            const codeCesu = normalizeCodeCesu((values as RegisterDraft | undefined)?.code_cesu);
            const authorizeSepa =
                codeCesu === CodeCesu.ADD_CESU
                    ? Array.isArray(rawSepa)
                        ? rawSepa.includes("1")
                        : Boolean(rawSepa)
                    : false;
            const authorizeAdmin = Array.isArray(rawAdmin) ? rawAdmin.includes("1") : Boolean(rawAdmin);

            // Never mutate `values` (Final Form uses it as live form state)
            const submitValues = Object.assign({}, values, {
                code_cesu: codeCesu,
                authorize_sepa: authorizeSepa,
                authorize_admin: authorizeAdmin,
            });

            const data = await validateRegisterClientCesu(submitValues as RegisterDraft);
            await createRegisterUser(data as HashMap<unknown>);
            trackRegisterStepComplete({
                registerType: "client",
                stepKey: "cesu",
                stepIndex: 4,
                stepLabel: intl.formatMessage("authentication.register.client_flow.cesu.label"),
            });
            stepper.goTo("confirmation");
        } catch (err: unknown) {
            const payloadError = getPayloadErrorFromResponse(err);
            if (payloadError?.message && payloadError.description) {
                toast.error(payloadError.message, { description: payloadError.description });
            } else if (payloadError?.description) {
                toast.error(payloadError.description);
            } else if (payloadError?.message) {
                toast.error(payloadError.message);
            }

            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    ...(client ?? {}),
                    code_cesu: normalizeCodeCesu((client as RegisterDraft | undefined)?.code_cesu),
                    authorize_sepa: Array.isArray((client as Record<string, unknown> | undefined)?.authorize_sepa)
                        ? (client as Record<string, unknown>).authorize_sepa
                        : (client as Record<string, unknown> | undefined)?.authorize_sepa
                          ? ["1"]
                          : [],
                    authorize_admin: Array.isArray((client as Record<string, unknown> | undefined)?.authorize_admin)
                        ? (client as Record<string, unknown>).authorize_admin
                        : (client as Record<string, unknown> | undefined)?.authorize_admin
                          ? ["1"]
                          : [],
                } as RegisterDraft}
                mutators={{ ...arrayMutators }}
                validate={(values: RegisterDraft) => {
                    const errors: Record<string, string> = {};
                    const iban = String(values?.iban ?? "");
                    const bic = String(values?.bic ?? "");

                    if (iban.trim() && !isValidFrenchIban(iban)) {
                        errors.iban = "IBAN français invalide.";
                    }

                    if (bic.trim() && !isValidFrenchBic(bic)) {
                        errors.bic = "BIC français invalide.";
                    }

                    return errors;
                }}
                render={({ handleSubmit, submitting, values }) => (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <p className="h1 font-black">
                                Cadre légal & <span className="block text-primary">protection sociale.</span>
                            </p>
                            <p className="h4 margin-bottom--2 margin-top--1">
                                Twoghether s'appuie sur le CESU pour vous garantir un emploi déclaré, sans complexité. Vous êtes couvert, votre intervenant aussi.
                            </p>

                            <div className="register__group-fields">
                                <div className="grid--12 register__grid grid--has-gutter-3x">
                                    <div className="grid__col--12 grid__col--small-12">
                                        <div className="w-full rounded-[28px] border border-primary/10 bg-primary/5 p-6 sm:p-7">
                                            <div className="flex items-start gap-3">
                                                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                                                    <HelpCircle className="h-4 w-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-sm font-black">C'est quoi le CESU ?</div>
                                                    <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                                        Le Chèque Emploi Service Universel est un dispositif de l'URSSAF qui
                                                        simplifie les démarches pour les services à la personne. C'est
                                                        l'assurance d'être <span className="text-primary font-semibold">100% légal</span>, d'avoir une{" "}
                                                        <span className="text-primary font-semibold">couverture santé</span>, de cotiser pour votre{" "}
                                                        <span className="text-primary font-semibold">retraite</span> et d'être assuré en cas d'accident.
                                                    </div>
                                                </div>
                                                <div className="hidden sm:block opacity-20">
                                                    <ShieldCheck className="h-12 w-12 text-primary" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid__col--12 grid__col--small-12">
                                        <div className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                                            Avez-vous déjà un numéro CESU employeur ?
                                        </div>
                                    </div>

                                    <div className="grid__col--12 grid__col--small-12">
                                        <Radio
                                            inputProps={{
                                                name: "code_cesu",
                                                required: true,
                                                className: "sr-only",
                                                groupClassName: cesuRadioGroupClassName,
                                                labelClassName: cesuRadioLabelClassName,
                                                options: [
                                                    {
                                                        value: CodeCesu.HAS_CESU,
                                                        label: (
                                                            <CesuRadioOptionLabel
                                                                icon={<CheckCircle className="h-4 w-4" />}
                                                                title="Oui, j'en ai un"
                                                                subtitle="Je possède déjà mon numéro national d'employeur URSSAF."
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        value: CodeCesu.ADD_CESU,
                                                        label: (
                                                            <CesuRadioOptionLabel
                                                                icon={<X className="h-4 w-4" />}
                                                                title="Non, pas encore"
                                                                subtitle="Il me sera attribué automatiquement lors de la création de mon compte."
                                                            />
                                                        ),
                                                    },
                                                ],
                                            }}
                                            fieldControlProps={{
                                                className: "grid gap-4 sm:grid-cols-2",
                                            }}
                                        />
                                    </div>

                                    {normalizeCodeCesu((values as RegisterDraft | undefined)?.code_cesu) ===
                                        CodeCesu.HAS_CESU && (
                                        <div className="grid__col--12 grid__col--small-12">
                                            <TextField
                                                inputProps={{
                                                    name: "num_cesu",
                                                    required: true,
                                                    placeholder: "XXX XXX XXX XXX",
                                                    minLength: 14,
                                                    maxLength: 14,
                                                }}
                                                labelProps={{
                                                    label: "N° CESU",
                                                    className: "font-black"
                                                }}
                                            />
                                            <div className="mt-2 text-xs text-muted-foreground">
                                                Vous pouvez retrouver votre n° CESU dans votre espace personnel sur le site du CESU.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="register__group-fields">
                                <div className="w-full rounded-[28px] bg-muted/30 p-6 sm:p-7">
                                    {normalizeCodeCesu((values as RegisterDraft | undefined)?.code_cesu) ===
                                        CodeCesu.ADD_CESU && (
                                        <>
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                                                    <Landmark className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <div className="text-lg font-black">Mandat SEPA</div>
                                                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                                        Compte bancaire
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-6 space-y-5">
                                                <TextField
                                                    inputProps={{
                                                        name: "account_name",
                                                        required: true,
                                                        placeholder: "M. Jean Dupont",
                                                        maxLength: 255,
                                                        className:
                                                            "field-control__input h-14 rounded-[22px] border border-input bg-background px-6 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
                                                    }}
                                                    labelProps={{
                                                        label: "Nom complet du titulaire du compte",
                                                        className:
                                                            "text-[11px] font-black uppercase tracking-wider text-foreground",
                                                    }}
                                                />

                                                <TextField
                                                    inputProps={{
                                                        name: "iban",
                                                        required: true,
                                                        placeholder: "FR76 3000 6000 0112 3456 7890 123",
                                                        maxLength: 34,
                                                        className:
                                                            "field-control__input h-14 rounded-[22px] border border-input bg-background px-6 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
                                                    }}
                                                    labelProps={{
                                                        label: "IBAN (présent sur votre RIB)",
                                                        className:
                                                            "text-[11px] font-black uppercase tracking-wider text-foreground",
                                                    }}
                                                />

                                                <TextField
                                                    inputProps={{
                                                        name: "bic",
                                                        required: true,
                                                        placeholder: "BNPAFRPPXXX",
                                                        maxLength: 11,
                                                        className:
                                                            "field-control__input h-14 rounded-[22px] border border-input bg-background px-6 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
                                                    }}
                                                    labelProps={{
                                                        label: "BIC (présent sur votre RIB)",
                                                        className:
                                                            "text-[11px] font-black uppercase tracking-wider text-foreground",
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="space-y-4 pt-1">
                                        {normalizeCodeCesu((values as RegisterDraft | undefined)?.code_cesu) ===
                                            CodeCesu.ADD_CESU && (
                                            <Checkbox
                                                inputProps={{
                                                    name: "authorize_sepa",
                                                    options: [
                                                        {
                                                            value: "1",
                                                            label: "J'autorise l'URSSAF à prélever automatiquement les cotisations sociales sur mon compte bancaire via un mandat SEPA.",
                                                        },
                                                    ],
                                                    className: "mt-0.5 h-5 w-5 shrink-0 rounded-md border border-input bg-background",
                                                    groupClassName: "mt-6 flex items-start gap-3",
                                                    labelClassName: "cursor-pointer text-xs text-muted-foreground",
                                                }}
                                            />
                                        )}

                                        <Checkbox
                                            inputProps={{
                                                name: "authorize_admin",
                                                options: [
                                                    {
                                                        value: "1",
                                                        label: "J'autorise Twoghether à effectuer les démarches administratives en mon nom (déclarations auprès de l'URSSAF).",
                                                    },
                                                ],
                                                className: "mt-0.5 h-5 w-5 shrink-0 rounded-md border border-input bg-background",
                                                groupClassName: "flex items-start gap-3",
                                                labelClassName: "cursor-pointer text-xs text-muted-foreground",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="w-full rounded-[28px] border border-primary/10 bg-primary/5 px-6 py-5 text-sm text-muted-foreground">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                                        <Info className="h-4 w-4" />
                                    </div>
                                    <p className="min-w-0 leading-relaxed">
                                        <span className="font-black text-foreground/90">Rassurez-vous :</span>{" "}
                                        Twoghether s'occupe de faire le lien avec l'URSSAF. Vous n'avez aucune démarche
                                        administrative complexe à faire.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setClient(Object.assign({}, client, values));
                                        stepper.goTo("informations");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <Button
                                    className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full px-10 py-4 font-black"
                                    type="submit"
                                    disabled={
                                        (() => {
                                            const rawSepa = (values as Record<string, unknown>)?.authorize_sepa;
                                            const rawAdmin = (values as Record<string, unknown>)?.authorize_admin;
                                            const authorizeSepaChecked = Array.isArray(rawSepa)
                                                ? rawSepa.includes("1")
                                                : Boolean(rawSepa);
                                            const authorizeAdminChecked = Array.isArray(rawAdmin)
                                                ? rawAdmin.includes("1")
                                                : Boolean(rawAdmin);

                                            const codeCesu = normalizeCodeCesu(
                                                (values as RegisterDraft | undefined)?.code_cesu,
                                            );

                                            return (
                                        submitting ||
                                        !codeCesu ||
                                        (codeCesu === CodeCesu.ADD_CESU && (
                                            !values?.account_name ||
                                            !values?.iban ||
                                            !isValidFrenchIban(values?.iban as string) ||
                                            !values?.bic ||
                                            !isValidFrenchBic(String((values as RegisterDraft)?.bic ?? "")) ||
                                            !authorizeSepaChecked ||
                                            !authorizeAdminChecked
                                        )) ||
                                        (codeCesu === CodeCesu.HAS_CESU && (!values?.num_cesu ||
                                            !authorizeAdminChecked))
                                            );
                                        })()
                                    }
                                >
                                    {intl.formatMessage("generic.step.next")}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                    </form>
                )}
            />
        </React.Fragment>
    );
};

export default RegisterClientCesu;
